import { ReactNode } from "react";
import { ViewerState } from "./protocol";
import { useAppLanguage } from "./i18n";

interface DriverIdentity {
  number: string;
  driver: string;
  color: string;
}

export interface Stage2RaceShellSlots {
  storyOverlay?: ReactNode;
  emotionMap?: ReactNode;
  courseLines?: ReactNode;
  telemetry?: ReactNode;
  conversation?: ReactNode;
  caption?: ReactNode;
}

interface Stage2RaceShellProps {
  state: ViewerState;
  identity: DriverIdentity;
  networkHref: string;
  status: ReactNode;
  video: ReactNode;
  controls: ReactNode;
  observation: ReactNode;
  conversationEngaged?: boolean;
  slots?: Stage2RaceShellSlots;
}

function ReservedSlot({
  testId,
  eyebrow,
  title,
  description,
}: {
  testId: string;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <section className="stage2-slot stage2-slot-pending" data-testid={testId}>
      <span>{eyebrow}</span>
      <strong>{title}</strong>
      <p>{description}</p>
    </section>
  );
}

export function Stage2RaceShell({
  state,
  identity,
  networkHref,
  status,
  video,
  controls,
  observation,
  conversationEngaged = false,
  slots = {},
}: Stage2RaceShellProps) {
  const { t } = useAppLanguage();
  return (
    <main
      className="stage2-race-shell"
      data-testid="stage2-race-shell"
      data-car-id={state.carId}
      data-seek-generation={state.seekGeneration}
      data-play-state={state.playState}
      data-conversation-engaged={conversationEngaged ? "true" : "false"}
      style={{ "--car-color": identity.color } as React.CSSProperties}
    >
      <section className="stage2-upper-grid">
        <section className="stage2-pane stage2-video-pane" aria-label={t("shell.onboard")}>
          <div className="stage2-video-frame">
            {video}
            {slots.storyOverlay ?? null}
            <div className="stage2-caption-slot" data-testid="stage2-caption-slot">
              {slots.caption ?? null}
            </div>
          </div>
          {controls}
        </section>

        <section
          className="stage2-pane stage2-emotion-slot"
          data-testid="stage2-emotion-map-slot"
          aria-label={t("shell.emotion")}
        >
          {slots.emotionMap ?? <ReservedSlot
            testId="stage2-emotion-map-pending"
            eyebrow="DRIVER × VEHICLE"
            title={t("shell.emotionTitle")}
            description={t("shell.emotionDescription")}
          />}
        </section>
      </section>

      <section className="stage2-lower-grid">
        <section
          className="stage2-pane stage2-course-slot"
          data-testid="stage2-course-lines-slot"
          aria-label={t("shell.course")}
        >
          {slots.courseLines ?? <ReservedSlot
            testId="stage2-course-lines-pending"
            eyebrow="SUZUKA"
            title={t("shell.courseTitle")}
            description={t("shell.courseDescription")}
          />}
        </section>

        <section className="stage2-pane stage2-conversation-slot" aria-label={t("shell.conversation")}>
          {slots.conversation ?? <ReservedSlot
            testId="stage2-conversation-slot"
            eyebrow="VEHICLE AI"
            title={t("shell.conversationTitle")}
            description={t("shell.conversationDescription")}
          />}
        </section>

        <section
          className="stage2-pane stage2-telemetry-slot"
          data-testid="stage2-telemetry-slot"
          aria-label={t("shell.telemetry")}
        >
          {slots.telemetry ?? <ReservedSlot
            testId="stage2-telemetry-pending"
            eyebrow="DRIVING DATA"
            title={t("shell.telemetryTitle")}
            description={t("shell.telemetryDescription")}
          />}
        </section>
      </section>

      <div className="stage2-runtime-foundation" aria-hidden="true">
        <a href={networkHref} tabIndex={-1}>NETWORK VIEW</a>
        {status}
        {observation}
      </div>
    </main>
  );
}
